import React from 'react';
import PropTypes from 'prop-types';

const DATE_WITH_OFFSET_REGEX = /(Z|[+-]\d{2}:\d{2})$/i;
const DATE_TIME_LOCAL_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

const getOffsetForTimeZone = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const valueByType = {};
  parts.forEach((part) => {
    if (part.type !== "literal") valueByType[part.type] = part.value;
  });

  const asUtc = Date.UTC(
    Number(valueByType.year),
    Number(valueByType.month) - 1,
    Number(valueByType.day),
    Number(valueByType.hour),
    Number(valueByType.minute),
    Number(valueByType.second),
    0
  );

  return asUtc - date.getTime();
};

const convertDateTimeLocalToUtcIso = (dateTimeLocalValue, timeZone) => {
  const matches = DATE_TIME_LOCAL_REGEX.exec(dateTimeLocalValue);
  if (!matches) return "";

  const year = Number(matches[1]);
  const month = Number(matches[2]);
  const day = Number(matches[3]);
  const hour = Number(matches[4]);
  const minute = Number(matches[5]);

  const wallClockUtc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  let timestamp = wallClockUtc;

  // Iterate to handle DST transitions for the requested zone.
  for (let i = 0; i < 4; i += 1) {
    const offset = getOffsetForTimeZone(new Date(timestamp), timeZone);
    const adjusted = wallClockUtc - offset;
    if (adjusted === timestamp) break;
    timestamp = adjusted;
  }

  return new Date(timestamp).toISOString();
};

const DateTimeInput = ({ className, name, value, onChange, disabled, placeholder, utc = false, timeZone }) => {
  const handleChange = (event) => {
    var name = event.target.name;
    var value = event.target.value;

    if (value == '') {
      onChange(name, value);
    } else {
      try {
        const zone = timeZone && timeZone !== "local"
          ? timeZone
          : Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (utc) {
          const date = new Date(value);
          const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes()));
          onChange(name, utcDate.toISOString());
        } else if (zone) {
          onChange(name, convertDateTimeLocalToUtcIso(value, zone));
        } else {
          const date = new Date(value);
          onChange(name, date.toISOString());
        }
      } catch {
        //Do Nothing
      }
    }
  };

  let inputValue = '';
  let tValue = (value ?? '') == '' ? placeholder : value;
  if (tValue) {
    if (tValue && !DATE_WITH_OFFSET_REGEX.test(tValue)) {
      tValue += "Z";
    }

    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const zone = timeZone && timeZone !== "local" ? timeZone : localTimezone;

    // Parse the UTC time and convert it to the local time
    const dateObject = tValue ? new Date(tValue) : new Date();
    const localDate = utc ?
      new Date(dateObject.toLocaleString('en-US', { timeZone: "utc" })) :
      new Date(dateObject.toLocaleString('en-US', { timeZone: zone }));

    // Format the local date as a string for the input value
    inputValue = formatDateTime(localDate);
  }

  var style = (value ?? '') == '' ? { color: '#a5a9b1' } : {};
  return <input type="datetime-local" className={className ?? 'form-control'} placeholder={placeholder ?? ''} name={name} value={inputValue} disabled={disabled} onChange={handleChange} autoComplete='off' style={style} />
}

const formatDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default DateTimeInput;

DateTimeInput.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  utc: PropTypes.bool,
  timeZone: PropTypes.string,
}
